/* ============================================================================
   TFMC — ONE MOTION FILE.  (Phase 19, 4 September 2026)
   ----------------------------------------------------------------------------
   Replaces four near-identical scripts: tfmc-ez-carousel.js (the buy-box quote
   rail), tfmc-sec-rail.js (materials, the seven signs, the range),
   tfmc-sec-reviews.js (the reviews rail behind its reveal) and
   tfmc-ez-gallery.js (the hero gallery). They had drifted into four copies of
   one nearest-slide search, one dot sync, one chevron end-test and one resize
   debounce, and each had fixed a bug the other three still carried — the
   last-dot end test was in two of them, the <details> re-measure in one.

   It also carries the three things this page did not have: the swipe isolation
   below, the gallery's scrub rail, and the thumbnail strip keeping its active
   thumb in view.

   THE EXPANDERS ARE NOT HERE, AND THAT IS THE POINT. All six are pure CSS —
   see EXPAND / COLLAPSE in assets/tfmc-pdp-tokens.css. No measurement, no
   wiring, nothing to keep in sync.

   ----------------------------------------------------------------------------
   WHY EVERY RAIL NEEDED A SECOND GRAB — the finding this phase exists for

   assets/theme.js line 1044 does this, on every page of the theme:

       SwipeListener(document, { preventScroll: ({detail:t}) =>
           2 * Math.abs(t.y[0]-t.y[1]) < Math.abs(t.x[0]-t.x[1]) })

   assets/swipe-listener.min.js registers its touchmove handler with
   `{passive: !options.preventScroll}` — and preventScroll here is a function,
   so that resolves to `{passive: false}`. From the SECOND touchmove of every
   gesture the library evaluates the predicate and, when the gesture is more
   horizontal than vertical by a factor of two, calls preventDefault().

   A horizontal flick anywhere on the page is therefore cancelled before the
   compositor can start scrolling — and the cancellation is non-deterministic,
   because whether the first two samples read as "decisively horizontal"
   depends on the angle the finger happened to start at. A dead flick, then a
   working one: exactly "I have to grab it twice".

   The listener is in a fenced file and is not ours to remove. The defence is
   the one snippets/mobile-gallery.liquid has used since February — stop the
   touchmove at the scroll container so it never reaches the document. That is
   why the live theme's gallery feels right and every rail we built did not,
   and it is the whole of the difference between them in JavaScript.

   The listener we add is PASSIVE and calls nothing. After this file runs there
   is no non-passive touch listener on any scroll container on the page.

   ----------------------------------------------------------------------------
   THREE RULES CARRIED OVER FROM THE FILES THIS REPLACES

   - Page-level listeners are bound ONCE at module scope and walk the document.
     shopify:section:load replaces markup on every theme-editor edit, so a
     listener bound inside a per-element wire() accumulates one copy per edit
     and its closure keeps the detached node alive.
   - Geometry is measured on demand, never cached: every rail changes shape at
     750px, and a rail inside a closed <details> measures zero.
   - Nothing here touches, reads or writes the Loop widget.

   Idempotent: a second execution is a no-op.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__tfmcMotion) return;
  window.__tfmcMotion = true;

  function slice(l) { return Array.prototype.slice.call(l); }

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  /* Read live rather than captured once: a visitor can change the setting with
     the page open, and every branch below is cheap enough to ask each time. */
  function reduced() { return !!(mq && mq.matches); }
  function ease() { return reduced() ? 'auto' : 'smooth'; }

  /* ══ 1 · SWIPE ISOLATION ═══════════════════════════════════════════════════
     Every horizontal scroll container in the zone, including the journal's,
     which has no controls and no other script. Adding a rail means adding it
     here — or putting [data-tfmc-scroller] on it. */
  var SCROLLERS = '.dk-gal__track,.dk-gal__rail,.ez-q__track,.dk-lab__track,' +
                  '.dk-mats,.dk-rev__track,.dk-rng__track,.dk-art__grid,' +
                  '[data-tfmc-scroller]';

  function stopSwipe(e) { e.stopPropagation(); }

  function isolateAll() {
    var els = document.querySelectorAll(SCROLLERS);
    for (var i = 0; i < els.length; i++) {
      if (els[i].__tfmcIso) continue;
      els[i].__tfmcIso = true;
      els[i].addEventListener('touchmove', stopSwipe, { passive: true });
    }
  }

  /* ══ 2 · THE RAIL ENGINE ═══════════════════════════════════════════════════
     One wiring for four markups. The four differ only in their attribute
     prefix, their page size and three optional extras, so those are the
     configuration and everything else is shared.

     Dots are indexed by POSITION, not by the number in their attribute. Every
     one of the four emits them in order with forloop.index0, so the two agree
     today; position is what cannot drift if a future rail forgets. */
  var RAILS = [
    { root: '[data-tfmc-gallery]',      track: '[data-gal-track]',  key: 'gal',
      live: '[data-gal-live]', thumbs: true, scrub: true },
    { root: '[data-tfmc-ez-carousel]',  track: '.ez-q__track',      key: 'ez-q' },
    { root: '[data-tfmc-rail]',         track: '[data-rail-track]', key: 'rail',
      page: 'data-rail-page' },
    { root: '[data-tfmc-rev-carousel]', track: '.dk-rev__track',    key: 'rev',
      page: 'data-rev-page', pageDefault: 3 }
  ];

  /* Scroll a strip so a child sits in the middle of it. Used by the thumbnail
     rail, which past the visible count used to leave the active thumb off the
     end of the strip with no way to reach it. */
  function centre(strip, child, smooth) {
    if (!strip || !child) return;
    if (strip.scrollWidth - strip.clientWidth < 2) return;
    strip.scrollTo({
      left: child.offsetLeft - strip.offsetLeft
            - strip.clientWidth / 2 + child.offsetWidth / 2,
      behavior: smooth && !reduced() ? 'smooth' : 'auto'
    });
  }

  function wireRail(root, cfg) {
    if (root.dataset.tfmcWired === 'true') return;
    var track = root.querySelector(cfg.track);
    if (!track) return;
    var slides = slice(track.children);
    if (!slides.length) return;
    root.dataset.tfmcWired = 'true';

    var dots  = slice(root.querySelectorAll('[data-' + cfg.key + '-dot]'));
    var navs  = slice(root.querySelectorAll('[data-' + cfg.key + '-nav]'));
    var thumbStrip = cfg.thumbs ? root.querySelector('.dk-gal__rail') : null;
    var thumbs = cfg.thumbs ? slice(root.querySelectorAll('[data-gal-thumb]')) : [];
    var live   = cfg.live ? root.querySelector(cfg.live) : null;

    /* The server rendered the dots from this number, so reading it back is the
       only way the two cannot drift apart. */
    var per = cfg.page ? parseInt(root.getAttribute(cfg.page), 10) : 1;
    if (!per || per < 1) per = cfg.pageDefault || 1;
    var pages = Math.ceil(slides.length / per);

    /* THE LAST SLIDE. A rail runs out of scroll before its last card reaches
       the left edge — measured on the materials rail, 606px of travel against a
       card starting 732px in — so a bare nearest-slide search can never return
       the last index and its dot could never light. At maximum scroll the last
       card is nevertheless fully on screen, so the end of the range wins.

       `max > 1` matters: above 750px some of these tracks are grids with
       nothing to scroll, where scrollWidth equals clientWidth and a bare end
       test is trivially true — which would light the LAST dot where the first
       is the honest answer. */
    function slideIndex() {
      var max = track.scrollWidth - track.clientWidth;
      if (max > 1 && track.scrollLeft >= max - 1) return slides.length - 1;
      var best = 0, bestD = Infinity;
      for (var i = 0; i < slides.length; i++) {
        /* offsetLeft is measured against the OFFSET PARENT, and these tracks
           carry padding, so the track's own offsetLeft is not zero. */
        var d = Math.abs(slides[i].offsetLeft - track.offsetLeft - track.scrollLeft);
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    }

    function mark(list, on, cls) {
      for (var i = 0; i < list.length; i++) {
        var is = i === on;
        list[i].classList.toggle(cls, is);
        if (is) list[i].setAttribute('aria-current', 'true');
        else list[i].removeAttribute('aria-current');
      }
    }

    var said = -1;
    function announce(i) {
      if (!live || i === said) return;
      said = i;
      /* Reuse the slide's own label so the spoken sentence and the slide's
         accessible name cannot drift. */
      live.textContent = 'Image ' +
        (slides[i].getAttribute('aria-label') || (i + 1) + ' of ' + slides.length);
    }

    function sync() {
      var i = slideIndex();
      var p = Math.min(pages - 1, Math.floor(i / per));
      mark(dots, p, 'is-active');
      if (thumbs.length) {
        mark(thumbs, i, 'dk-railthumb--on');
        /* Keep the active thumbnail in view. Not while the visitor is dragging
           the strip itself, or the strip fights the finger. */
        if (!thumbStrip || !thumbStrip.__dragging) centre(thumbStrip, thumbs[i], true);
      }
      announce(i);
      /* Disabled at each end rather than wrapping, so the control tells the
         truth about where you are. The test is on scroll POSITION, not on page
         index: the last page is often partly filled and a page-index test would
         grey the chevron out while there was still rail to travel. */
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      for (var n = 0; n < navs.length; n++) {
        var d = parseInt(navs[n].getAttribute('data-' + cfg.key + '-nav'), 10) || 0;
        navs[n].disabled = d < 0 ? atStart : atEnd;
      }
    }

    function goTo(i, instant) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({
        left: slides[i].offsetLeft - track.offsetLeft,
        behavior: instant ? 'auto' : ease()
      });
      return i;
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      /* One layout read per frame, never one per scroll event. */
      window.requestAnimationFrame(function () { ticking = false; sync(); });
    }, { passive: true });

    dots.forEach(function (dot, p) { dot.addEventListener('click', function () { goTo(p * per); }); });
    thumbs.forEach(function (b, i) { b.addEventListener('click', function () { goTo(i); }); });
    navs.forEach(function (n) {
      n.addEventListener('click', function () {
        var d = parseInt(n.getAttribute('data-' + cfg.key + '-nav'), 10) || 0;
        goTo(slideIndex() + d * per);
      });
    });

    /* Left/right on the focused track. preventDefault here stops the arrow key
       ALSO scrolling the page, which is required, and this is a keydown — the
       one place in this file a default is cancelled. */
    track.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      goTo(slideIndex() + (e.key === 'ArrowRight' ? per : -per));
    });

    if (cfg.scrub) wireScrub(root, slides, goTo);

    /* A rail inside a closed <details> has no layout: every offsetLeft reads 0
       and any page arithmetic done then is wrong. Measure when it opens. */
    var details = root.closest('details');
    if (details) {
      details.addEventListener('toggle', function () { if (details.open) sync(); });
      if (details.open) sync();
    } else {
      sync();
    }
    root.__tfmcSync = sync;
  }

  /* ══ 3 · THE SCRUB RAIL ════════════════════════════════════════════════════
     George: "press and drag along it to move rapidly through the images, with
     the image tracking the finger", like an iPhone photo scrubber.

     Pointer events, not touch events — so this adds no touch listener at all
     and the gate above stays true. The rail carries `touch-action: pan-y` in
     CSS, which is what tells the browser a horizontal drag here is ours and a
     vertical one still scrolls the page; doing that with preventDefault on a
     non-passive touchmove is the very thing this phase removed.

     The jump is `behavior: 'auto'` deliberately. A smooth scroll per sample
     would queue animations behind the finger and the picture would lag it. */
  function wireScrub(root, slides, goTo) {
    var rail = root.querySelector('.dk-gal__dots');
    if (!rail || slides.length < 2) return;

    var last = -1, downX = 0, dragging = false, pid = null;
    var SLOP = 4;

    function indexAt(clientX) {
      var r = rail.getBoundingClientRect();
      if (r.width <= 0) return 0;
      var f = (clientX - r.left) / r.width;
      return Math.max(0, Math.min(slides.length - 1, Math.round(f * (slides.length - 1))));
    }

    function tick(i) {
      if (i === last) return;
      last = i;
      goTo(i, true);
      /* A light tick as each image comes into view. Short — 8ms is a tap, not a
         buzz. Silent where the API is absent (every desktop browser, and iOS
         Safari, which has never shipped it) and silent under reduced motion,
         where a haptic makes the same promise an animation does. */
      if (!reduced() && navigator.vibrate) { try { navigator.vibrate(8); } catch (err) {} }
    }

    /* NOTHING HAPPENS ON PRESS. A tick is a <button> with its own "N of M"
       label and tapping one must keep selecting that image — so the scrub only
       begins once the finger has travelled past the slop, and only then is the
       pointer captured. A tap therefore never captures, never scrubs and
       reaches the button exactly as it did before. */
    rail.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      downX = e.clientX; dragging = false; pid = e.pointerId; last = -1;
    });

    rail.addEventListener('pointermove', function (e) {
      if (pid === null || e.pointerId !== pid) return;
      if (!dragging) {
        if (Math.abs(e.clientX - downX) < SLOP) return;
        dragging = true;
        rail.classList.add('is-scrubbing');
        if (rail.setPointerCapture) { try { rail.setPointerCapture(pid); } catch (err) {} }
      }
      tick(indexAt(e.clientX));
    });

    function end() {
      if (pid !== null && dragging) {
        rail.classList.remove('is-scrubbing');
        /* The release of a drag that started on a tick would otherwise fire
           that tick's click as well and jump the rail a second time. */
        rail.__justScrubbed = true;
        window.setTimeout(function () { rail.__justScrubbed = false; }, 0);
      }
      pid = null; dragging = false; last = -1;
    }
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);

    rail.addEventListener('click', function (e) {
      if (rail.__justScrubbed) { e.stopPropagation(); e.preventDefault(); }
    }, true);
  }

  /* ══ 4 · THE THUMBNAIL STRIP ═══════════════════════════════════════════════
     It scrolls, so a drag on it must not fight the sync above.

     THE RELEASE IS ONE MODULE-SCOPE LISTENER, not one per strip. The first
     version bound `window.addEventListener('pointerup', …)` inside the per-strip
     loop, which is precisely the accumulation this file's own header forbids:
     shopify:section:load replaces the markup, so every theme-editor edit gave
     the new strip a fresh window listener whose closure kept the detached old
     one alive. Caught by the adversarial review. The listener below is bound
     once, at module scope, and walks whatever is in the document at the time. */
  function wireThumbStrip() {
    var strips = document.querySelectorAll('.dk-gal__rail');
    for (var i = 0; i < strips.length; i++) {
      if (strips[i].__tfmcStrip) continue;
      strips[i].__tfmcStrip = true;
      strips[i].addEventListener('pointerdown', function (e) { e.currentTarget.__dragging = true; });
    }
  }
  window.addEventListener('pointerup', function () {
    var strips = document.querySelectorAll('.dk-gal__rail');
    for (var i = 0; i < strips.length; i++) strips[i].__dragging = false;
  }, { passive: true });

  /* ══ 5 · INIT ══════════════════════════════════════════════════════════════ */
  function init() {
    isolateAll();
    wireThumbStrip();
    for (var r = 0; r < RAILS.length; r++) {
      var roots = document.querySelectorAll(RAILS[r].root);
      for (var i = 0; i < roots.length; i++) wireRail(roots[i], RAILS[r]);
    }
  }

  /* ==========================================================================
     GALLERY STICKY OFFSET  (5 September 2026)
     --------------------------------------------------------------------------
     The gallery column is position:sticky with top: var(--tfmc-gal-top, 136px).
     136px is right whenever the whole gallery fits under the header. It is
     wrong on a short window: the column pins immediately, and everything below
     the fold — the foot of the picture and the whole thumbnail strip — can
     never be reached, because the right-hand column is what scrolls.

     So the offset is measured. When the gallery is taller than the space under
     the header, the top goes NEGATIVE by exactly the overflow, which lets the
     column travel up with the page until its foot is FOOT_GAP above the
     viewport bottom, and pin there. When it fits, the offset is 136px and the
     behaviour is what it was.

     Reads only offsetHeight and innerHeight, and writes one custom property.
     No layout is written back inside the observer, so it cannot loop. */
  var GAL_TOP = 136;
  var FOOT_GAP = 24;

  function syncGalleryTop() {
    var col = document.querySelector('.tfmc-ez--gallery');
    if (!col) return;
    /* Below 750px the column is not sticky at all — the media query that
       carries position:sticky starts there — so leave the property unset. */
    if (!window.matchMedia('(min-width: 750px)').matches) {
      col.style.removeProperty('--tfmc-gal-top');
      return;
    }
    var top = Math.min(GAL_TOP, window.innerHeight - col.offsetHeight - FOOT_GAP);
    col.style.setProperty('--tfmc-gal-top', Math.round(top) + 'px');
  }

  if (window.ResizeObserver) {
    var galRO = new ResizeObserver(function () { syncGalleryTop(); });
    var galInit = function () {
      var col = document.querySelector('.tfmc-ez--gallery');
      if (col) galRO.observe(col);
      syncGalleryTop();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', galInit);
    else galInit();
    document.addEventListener('shopify:section:load', galInit);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* The theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', init);

  /* ONE resize listener for the page, however many rails are on it and however
     many times the editor has re-rendered them. Rails that have been replaced
     are no longer in the document, so they are not walked and nothing holds
     them alive. */
  var rt = null;
  window.addEventListener('resize', function () {
    if (rt) clearTimeout(rt);
    rt = setTimeout(function () {
      var all = document.querySelectorAll('[data-tfmc-gallery],[data-tfmc-ez-carousel],[data-tfmc-rail],[data-tfmc-rev-carousel]');
      for (var i = 0; i < all.length; i++) {
        if (typeof all[i].__tfmcSync === 'function') all[i].__tfmcSync();
      }
      syncGalleryTop();
    }, 150);
  }, { passive: true });
})();
